import React, { useState, useEffect } from 'react';
import api_service from "../../network/api_callbacks";
import { toast } from 'react-toastify';
import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import storage, { LS_LOGIN } from "../../storage/storage";
import {
    Box,
    Button,
    InputAdornment,
    IconButton,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Grid,
    CircularProgress,
    Typography,
    FormControlLabel,
    Checkbox
} from "@mui/material";
import { styled } from '@mui/material/styles';
import Header from "../../components/Header";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.43,
    display: 'table-cell',
    verticalAlign: 'inherit',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    textAlign: 'left',
    padding: '16px',
    color: 'rgba(0, 0, 0, 0.87)',
    paddingBottom: '16px', // Increased padding
    paddingTop: '16px', // Increased padding
}));


const UserTransaction = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Modal State
    const [openModal, setOpenModal] = useState(false);
    const [selectedTxnDetails, setSelectedTxnDetails] = useState(null);

    const [filters, setFilters] = useState({
        userId: '',
        transactionId: '',
        status: '',
        paymentGateway: '',
        platform: '',
        eventType: '',
        type: '',
        currency: '',
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: '',
        reconcilePossible: false
    });

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: 7,
                ...filters
            };

            // Validation: Ensure Min <= Max for Amount
            if (params.minAmount && params.maxAmount && Number(params.minAmount) > Number(params.maxAmount)) {
                setLoading(false);
                toast.error("Min Amount cannot be greater than Max Amount");
                return;
            }

            // Validation: Ensure Start Date <= End Date
            if (params.startDate && params.endDate && new Date(params.startDate) > new Date(params.endDate)) {
                setLoading(false);
                toast.error("Start Date cannot be later than End Date");
                return;
            }

            // Remove empty filters and reconcilePossible (client-side filter)
            Object.keys(params).forEach(key => {
                // Keep 0 as valid value if needed, but here we just check for empty string
                if (params[key] === '' || params[key] === null || params[key] === undefined || key === 'reconcilePossible') {
                    delete params[key];
                }
            });

            const response = await api_service.getCurrencyTransactions(params);

            if (response && response.success) {
                let filteredData = response.data;

                // Apply client-side filter for reconcilable transactions
                if (filters.reconcilePossible) {
                    filteredData = filteredData.filter(txn => {
                        // Check if status is pending/failed
                        const isFailedOrPending = ['pending', 'failed'].includes(txn.status?.toLowerCase());

                        // Check if reconcile_status is 0 or not set
                        const isReconcilePending = txn.reconcile_status === 0 || txn.reconcile_status === undefined || txn.reconcile_status === null;

                        // Check if required fields exist in payment gateway
                        let hasRequiredFields = false;

                        if (txn.googlePlay) {
                            // For Google Play: must have purchaseToken with a value
                            hasRequiredFields = !!(txn.googlePlay.purchaseToken && txn.googlePlay.purchaseToken.trim() !== '');
                        } else if (txn.appleStore) {
                            // For Apple Store: must have transactionId OR originalTransactionId with a value
                            hasRequiredFields = !!(
                                (txn.appleStore.transactionId && txn.appleStore.transactionId.toString().trim() !== '') ||
                                (txn.appleStore.originalTransactionId && txn.appleStore.originalTransactionId.toString().trim() !== '')
                            );
                        }

                        return isFailedOrPending && isReconcilePending && hasRequiredFields;
                    });
                }

                setTransactions(filteredData);
                setTotalPages(response.pagination.pages);
                setTotalRecords(response.pagination.total);
            } else {
                setTransactions([]);
                // Toast/Error check could be here
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
            toast.error("Failed to fetch transactions");
        } finally {
            setLoading(false);
        }
    };

    // Debounce Effect for Dynamic Filtering
    useEffect(() => {
        const handler = setTimeout(() => {
            setPage(1); // Reset to page 1 on filter change
            fetchTransactions();
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
    }, [filters]); // Trigger on filters change

    useEffect(() => {
        fetchTransactions();
    }, [page]);

    // Disable body scroll for this page
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // handleApplyFilters Removed

    const handleClearField = (fieldName) => {
        setFilters(prev => ({ ...prev, [fieldName]: '' }));
    };

    const handleClearFilters = () => {
        setFilters({
            userId: '',
            transactionId: '',
            status: '',
            paymentGateway: '',
            platform: '',
            eventType: '',
            type: '',
            currency: '',
            startDate: '',
            endDate: '',
            minAmount: '',
            maxAmount: '',
            reconcilePossible: false
        });
        setPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            // Format: Dec 11 17:22:29 PM
            const month = date.toLocaleString('default', { month: 'short' });
            const day = date.getDate();
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${month} ${day} ${displayHours}:${minutes}:${seconds} ${ampm}`;
        } catch (e) {
            return 'Invalid Date';
        }
    };

    const handleViewDetails = (txn) => {
        let details = null;
        const platform = txn.platform ? txn.platform.toLowerCase() : '';

        if (platform === 'android' && txn.googlePlay) {
            details = txn.googlePlay;
        } else if (platform === 'ios' && txn.appleStore) {
            details = txn.appleStore;
        } else {
            // Fallback: try to find any details if platform mismatches or undefined
            details = txn.googlePlay || txn.appleStore || { message: "No specific details available" };
        }
        setSelectedTxnDetails(details);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedTxnDetails(null);
    };

    const handleResolve = async (txnId) => {
        try {
            const userData = storage.get(LS_LOGIN);
            // Fallback to 'admin_user' if name/username is not found, or use "Admin"
            const reconciledBy = userData?.name || userData?.username || 'admin_user';

            const baseUrl = process.env.REACT_APP_DASHBOARD_CLIENT_BASE_URL;
            if (!baseUrl) {
                toast.error("Client Base URL not configured");
                return;
            }

            const response = await fetch(`${baseUrl}/api/client/v1/reconcile-transaction`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    transactionId: txnId,
                    reconciledBy: reconciledBy
                })
            });

            const result = await response.json();

            // Check for success code 200
            if (result && result.responseCode === 200) {
                toast.success("Reconciliation Successful");
                fetchTransactions(); // Refresh data
            } else {
                toast.error(result?.data?.message || "Reconciliation Failed");
            }

        } catch (error) {
            console.error("Reconciliation error:", error);
            toast.error("Reconciliation Failed");
        }
    };

    return (

        <div id='user-transaction'
            style={{
                // height: '100vh',
                width: '100%',
                // overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '25px',
                maxWidth: '92%'
            }}>
            {/* <Box m="1.5rem 2.5rem" pt="80px"> */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <Header title="" subtitle="List of User Transactions" />

                {/* Filters Section - Static */}
                <div id='user-transaction-filters'
                    style={{
                        padding: '0 20px',
                        flexShrink: 0
                    }}>
                    <h2 style={{ textAlign: "left", marginLeft: "40%" }}>USER TRANSACTIONS</h2>
                    <Box
                        mt="10px"
                        mb="10px"
                        p="20px"
                        component={Paper}
                        elevation={3}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="User ID"
                                    name="userId"
                                    value={filters.userId}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.userId && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('userId')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Transaction ID"
                                    name="transactionId"
                                    value={filters.transactionId}
                                    onChange={handleFilterChange}
                                    size="small"
                                    placeholder="Search by Transaction ID"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.transactionId && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('transactionId')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel sx={{ "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } }}>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        endAdornment={filters.status && (
                                            <InputAdornment position="end" sx={{ mr: 2 }}>
                                                <IconButton onClick={() => handleClearField('status')} edge="end" size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    >
                                        <MenuItem value=""><em>All</em></MenuItem>
                                        <MenuItem value="success">Success</MenuItem>
                                        <MenuItem value="failed">Failed</MenuItem>
                                        <MenuItem value="initiated">Initiated</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel sx={{ "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } }}>Platform</InputLabel>
                                    <Select
                                        label="Platform"
                                        name="platform"
                                        value={filters.platform}
                                        onChange={handleFilterChange}
                                        endAdornment={filters.platform && (
                                            <InputAdornment position="end" sx={{ mr: 2 }}>
                                                <IconButton onClick={() => handleClearField('platform')} edge="end" size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    >
                                        <MenuItem value=""><em>All</em></MenuItem>
                                        <MenuItem value="android">Android</MenuItem>
                                        <MenuItem value="ios">iOS</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Event Type"
                                    name="eventType"
                                    value={filters.eventType}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.eventType && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('eventType')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel sx={{ "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } }}>Type</InputLabel>
                                    <Select
                                        label="Type"
                                        name="type"
                                        value={filters.type}
                                        onChange={handleFilterChange}
                                        endAdornment={filters.type && (
                                            <InputAdornment position="end" sx={{ mr: 2 }}>
                                                <IconButton onClick={() => handleClearField('type')} edge="end" size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    >
                                        <MenuItem value=""><em>All</em></MenuItem>
                                        <MenuItem value="CREDIT">CREDIT</MenuItem>
                                        <MenuItem value="DEBIT">DEBIT</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel sx={{ "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } }}>Currency</InputLabel>
                                    <Select
                                        label="Currency"
                                        name="currency"
                                        value={filters.currency}
                                        onChange={handleFilterChange}
                                        endAdornment={filters.currency && (
                                            <InputAdornment position="end" sx={{ mr: 2 }}>
                                                <IconButton onClick={() => handleClearField('currency')} edge="end" size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    >
                                        <MenuItem value=""><em>All</em></MenuItem>
                                        <MenuItem value="INR">INR</MenuItem>
                                        <MenuItem value="USD">USD</MenuItem>
                                        <MenuItem value="EUR">EUR</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>


                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Payment Gateway"
                                    name="paymentGateway"
                                    value={filters.paymentGateway}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.paymentGateway && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('paymentGateway')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3} md={2}>
                                <TextField
                                    fullWidth
                                    label="Min Amount"
                                    name="minAmount"
                                    type="number"
                                    value={filters.minAmount}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.minAmount && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('minAmount')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} md={2}>
                                <TextField
                                    fullWidth
                                    label="Max Amount"
                                    name="maxAmount"
                                    type="number"
                                    value={filters.maxAmount}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.maxAmount && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('maxAmount')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    name="startDate"
                                    type="date"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    value={filters.startDate}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputProps={{
                                        endAdornment: filters.startDate && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('startDate')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    name="endDate"
                                    type="date"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    value={filters.endDate}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputProps={{
                                        endAdornment: filters.endDate && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('endDate')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" gap="10px">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.reconcilePossible}
                                            onChange={(e) => setFilters(prev => ({ ...prev, reconcilePossible: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                    label="Reconcilable"
                                />
                                <Button variant="outlined" color="secondary" onClick={handleClearFilters}>Clear Filters</Button>
                            </Grid>
                        </Grid>
                    </Box>
                </div>
                {/* Data Table */}


                <div id='user-transaction-table'
                    style={{
                        marginTop: '20px',
                        flex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0 20px'
                    }}>
                    <TableContainer
                        component={Paper}
                        elevation={3}
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 100, maxWidth: 100 }}>User ID</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell sx={{ minWidth: 100, maxWidth: 100 }}>Transaction ID</TableCell>
                                    <TableCell>Platform</TableCell>
                                    <TableCell sx={{ minWidth: 100, maxWidth: 100 }}>Event Type</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Currency</TableCell>
                                    <TableCell sx={{ minWidth: 122 }}>Purchase Type</TableCell>
                                    <TableCell sx={{ minWidth: 80, maxWidth: 80 }}>Amount</TableCell>
                                    <TableCell>Transaction Status</TableCell>
                                    <TableCell>Reconcile Status</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Reconciled By</TableCell>
                                    <TableCell>Reconciled On</TableCell>
                                    <TableCell sx={{ minWidth: 100, maxWidth: 100 }}>Gateway</TableCell>
                                    <TableCell>Date</TableCell>

                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : transactions.length > 0 ? (
                                    transactions.map((txn) => (
                                        <TableRow key={txn._id} hover>
                                            <StyledTableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={txn.userId}>
                                                {txn.userId}
                                            </StyledTableCell>
                                            <StyledTableCell>{txn.userName || '-'}</StyledTableCell>
                                            <StyledTableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={txn._id}>
                                                {txn._id}
                                            </StyledTableCell>
                                            <StyledTableCell>{txn.platform || '-'}</StyledTableCell>
                                            <StyledTableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={txn.eventType}>
                                                {txn.eventType || '-'}
                                            </StyledTableCell>
                                            <StyledTableCell>{txn.type || 'CREDIT'}</StyledTableCell>
                                            <StyledTableCell>{txn.currency || '-'}</StyledTableCell>
                                            <StyledTableCell>
                                                {txn.platform === 'android' ? txn?.googlePlay?.purchaseType :
                                                    txn.platform === 'ios' ? txn?.appleStore?.purchaseType : '-'}
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ maxWidth: 80, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {txn.amount}
                                            </StyledTableCell>
                                            <StyledTableCell sx={{
                                                color: txn.status === 'Success' ? 'green' :
                                                    txn.status === 'failed' ? 'red' :
                                                        txn.status === 'pending' || txn.status === 'initiated' ? 'orange' : 'inherit',
                                                fontWeight: 'bold'
                                            }}>
                                                {txn.status && txn.status.toUpperCase()}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {(() => {
                                                    const rStatus = txn.reconcile_status;
                                                    // Default to 0 (Pending) if undefined/null
                                                    const effectiveStatus = (rStatus === undefined || rStatus === null) ? 0 : rStatus;

                                                    let statusText = 'Pending';
                                                    let statusColor = 'orange';

                                                    if (effectiveStatus === 1) {
                                                        statusText = 'Success';
                                                        statusColor = 'green';
                                                    } else if (effectiveStatus === -1) {
                                                        statusText = 'Failed';
                                                        statusColor = 'red';
                                                    }

                                                    return (
                                                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                                                            {statusText}
                                                        </span>
                                                    );
                                                })()}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {(() => {
                                                    // Check if status is pending/failed
                                                    const isFailedOrPending = ['pending', 'failed'].includes(txn.status?.toLowerCase());

                                                    // Check if reconcile_status is 0 or not set
                                                    const isReconcilePending = txn.reconcile_status === 0 || txn.reconcile_status === undefined || txn.reconcile_status === null;

                                                    // Check if required fields exist in payment gateway
                                                    let hasRequiredFields = false;

                                                    if (txn.googlePlay) {
                                                        // For Google Play: must have purchaseToken with a value
                                                        hasRequiredFields = !!(txn.googlePlay.purchaseToken && txn.googlePlay.purchaseToken.trim() !== '');
                                                    } else if (txn.appleStore) {
                                                        // For Apple Store: must have transactionId OR originalTransactionId with a value
                                                        hasRequiredFields = !!(
                                                            (txn.appleStore.transactionId && txn.appleStore.transactionId.toString().trim() !== '') ||
                                                            (txn.appleStore.originalTransactionId && txn.appleStore.originalTransactionId.toString().trim() !== '')
                                                        );
                                                    }

                                                    // Show reconcile button only if all conditions are met
                                                    if (isFailedOrPending && isReconcilePending && hasRequiredFields) {
                                                        return (
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={() => handleResolve(txn._id)}
                                                                sx={{ minWidth: 'auto', padding: '5px 10px' }}
                                                            >
                                                                Reconcile
                                                            </Button>
                                                        );
                                                    } else {
                                                        return <span style={{ color: 'gray', fontSize: '0.875rem' }}>N/A</span>;
                                                    }
                                                })()}
                                            </StyledTableCell>
                                            <StyledTableCell>{txn.reconciled_by || '-'}</StyledTableCell>
                                            <StyledTableCell>{txn.reconciled_on ? formatDate(txn.reconciled_on) : '-'}</StyledTableCell>
                                            <StyledTableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {txn.googlePlay ? 'Google Play' :
                                                            txn.appleStore ? 'Apple Store' :
                                                                txn.razorpay ? 'Razorpay' :
                                                                    txn.paymentGateway || '-'}
                                                    </span>

                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(txn)}
                                                        sx={{ color: 'black', flexShrink: 0 }}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell>{formatDate(txn.created_at || txn.createdAt)}</StyledTableCell>

                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center">No transactions found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                {/* Pagination */}
                <div style={{ flexShrink: 0, paddingBottom: '20px' }}>
                    <Box display="flex" justifyContent="center" mt="20px">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="secondary"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </div>
                {/* Details Modal */}
                {openModal && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 9999
                        }}
                        onClick={handleCloseModal}
                    >
                        <Paper
                            sx={{
                                p: 4,
                                maxWidth: '600px',
                                width: '90%',
                                maxHeight: '80vh',
                                overflowY: 'auto',
                                position: 'relative'
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent close on clicking modal content
                        >
                            <Typography variant="h5" mb={2}>Transaction Details</Typography>
                            <Box component="pre" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: '4px' }}>
                                {JSON.stringify(selectedTxnDetails, null, 2)}
                            </Box>
                            <Button onClick={handleCloseModal} sx={{ mt: 2 }} variant="contained">Close</Button>
                        </Paper>
                    </Box>
                )}
                {/* </Box> */}
            </div>
        </div>
    );
};

export default UserTransaction;
